import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import ApiURl from '../controllers/Api'

/* ============================================================
   COLORS
   ============================================================ */

const PAGE_BG = 'var(--user-bg, #E8D9D5)'
const MAROON = 'var(--user-maroon, #2B0000)'

const SLIDE_DURATION = 5000

/* ============================================================
   IMAGE URL
   ============================================================ */

function imageUrl(path) {
  if (!path) return ''

  return path.startsWith('http')
    ? path
    : `${ApiURl}${path}`
}

/* ============================================================
   HERO SLIDER
   ============================================================ */

export default function HeroSlider() {
  const [images, setImages] = useState([])
  const [active, setActive] = useState(0)

  const intervalRef = useRef(null)

  /* ==========================================================
     FETCH BANNERS FROM YOUR EXISTING API
     ========================================================== */

  useEffect(() => {
    let mounted = true

    fetch(`${ApiURl}getHomeBanners.php`)
      .then((response) => response.json())
      .then((data) => {
        if (
          !mounted ||
          !data.success ||
          !Array.isArray(data.banners) ||
          data.banners.length === 0
        ) {
          return
        }

        const formatted = data.banners.map((banner) => ({
          type:
            banner.media_type === 'video'
              ? 'video'
              : 'image',

          src: imageUrl(
            banner.media_url ||
            banner.media_path
          ),

          mobileSrc: imageUrl(
            banner.mobile_media_url ||
            banner.mobile_media_path ||
            banner.media_url ||
            banner.media_path
          ),

          alt:
            banner.title ||
            'SL Gold banner',

          title:
            banner.title || '',

          subtitle:
            banner.subtitle || '',
        }))

        setImages(formatted)
        setActive(0)
      })
      .catch((error) => {
        console.error(
          'Unable to load banners:',
          error
        )
      })

    return () => {
      mounted = false
    }
  }, [])

  /* ==========================================================
     AUTOPLAY
     ========================================================== */

  useEffect(() => {
    if (images.length <= 1) return undefined

    intervalRef.current = window.setInterval(() => {
      setActive((current) =>
        (current + 1) % images.length
      )
    }, SLIDE_DURATION)

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
      }
    }
  }, [images.length])

  /* ==========================================================
     RESET TIMER AFTER MANUAL CHANGE
     ========================================================== */

  const resetTimer = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
    }

    if (images.length <= 1) return

    intervalRef.current = window.setInterval(() => {
      setActive((current) =>
        (current + 1) % images.length
      )
    }, SLIDE_DURATION)
  }

  /* ==========================================================
     CHANGE SLIDE
     ========================================================== */

  const goTo = (index) => {
    if (!images.length) return

    const nextIndex =
      (index + images.length) %
      images.length

    setActive(nextIndex)
    resetTimer()
  }

  /* ==========================================================
     FIND SLIDE POSITION
     ========================================================== */

  const getPosition = (index) => {
    const total = images.length

    if (!total) return 'hidden'

    if (index === active) {
      return 'active'
    }

    const previous =
      (active - 1 + total) % total

    const next =
      (active + 1) % total

    if (index === previous) {
      return 'previous'
    }

    if (index === next) {
      return 'next'
    }

    return 'hidden'
  }

  /* ==========================================================
     OPTIONAL SMALL ENTRANCE
     ========================================================== */

  const sliderRef = useRef(null)

  useEffect(() => {
    if (!sliderRef.current || images.length === 0) {
      return
    }

    const prefersReducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

    if (prefersReducedMotion) return

    gsap.fromTo(
      sliderRef.current,
      {
        opacity: 0,
        y: 12,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: 'power3.out',
      }
    )
  }, [images.length])

  /* ==========================================================
     EMPTY STATE
     ========================================================== */

  if (!images.length) {
    return (
      <section
        className="
          w-full
          px-0
          py-3

          sm:py-4

          lg:py-5
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            mx-auto

            h-[155px]
            w-[82%]

            animate-pulse

            rounded-[10px]

            sm:h-[360px]
            sm:rounded-[14px]

            lg:h-[500px]
          "
          style={{
            backgroundColor:
              'rgba(43,0,0,0.08)',
          }}
        />
      </section>
    )
  }

  return (
    <section
      ref={sliderRef}
      className="
        relative
        w-full
        overflow-hidden

        pt-3
        pb-4

        sm:pt-4
        sm:pb-5

        lg:pt-5
        lg:pb-6
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      {/* ======================================================
          VIEWPORT
          ====================================================== */}

      <div
        className="
          relative
          w-full
          overflow-hidden
        "
      >
        {/* ====================================================
            SLIDER
            ==================================================== */}

        <div
          className="
            hero-carousel

            relative

            h-[155px]

            sm:h-[340px]

            md:h-[390px]

            lg:h-[455px]

            xl:h-[500px]

            2xl:h-[535px]
          "
        >
          {images.map((image, index) => {
            const position =
              getPosition(index)

            const isActive =
              position === 'active'

            const isPrevious =
              position === 'previous'

            const isNext =
              position === 'next'

            return (
              <button
                key={`${image.src}-${index}`}
                type="button"
                aria-label={
                  isActive
                    ? `Current banner ${index + 1}`
                    : `Show banner ${index + 1}`
                }
                onClick={() => {
                  if (isPrevious) {
                    goTo(active - 1)
                  }

                  if (isNext) {
                    goTo(active + 1)
                  }
                }}
                className={`
                  hero-carousel-slide
                  hero-carousel-${position}

                  absolute
                  top-0
                  left-1/2

                  h-full

                  overflow-hidden

                  border-0
                  p-0

                  rounded-[9px]

                  sm:rounded-[12px]

                  lg:rounded-[14px]

                  transition-all
                  duration-700

                  ease-[cubic-bezier(0.22,1,0.36,1)]

                  focus:outline-none
                `}
              >
                {/* ===========================================
                    IMAGE
                    =========================================== */}

                {image.type === 'video' ? (
                  <video
                    src={image.src}
                    muted
                    playsInline
                    autoPlay={isActive}
                    loop
                    preload="metadata"
                    className="
                      pointer-events-none

                      h-full
                      w-full

                      object-cover
                    "
                  />
                ) : (
                  <picture>
                    <source
                      media="(max-width: 640px)"
                      srcSet={
                        image.mobileSrc
                      }
                    />

                    <img
                      src={image.src}
                      alt={image.alt}
                      draggable="false"
                      className="
                        pointer-events-none

                        h-full
                        w-full

                        select-none

                        object-cover
                      "
                    />
                  </picture>
                )}

                {/* ===========================================
                    SIDE SLIDE SUBTLE TINT
                    =========================================== */}

                {!isActive && (
                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-0
                    "
                    style={{
                      background:
                        'rgba(43,0,0,0.06)',
                    }}
                  />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* ======================================================
          DIAMOND INDICATORS
          ====================================================== */}

      {images.length > 1 && (
        <div
          className="
            mt-3

            flex
            items-center
            justify-center

            gap-[11px]

            sm:mt-4
            sm:gap-[13px]

            lg:mt-5
          "
        >
          {images.map((image, index) => {
            const isActive =
              index === active

            return (
              <button
                key={`dot-${image.src}-${index}`}
                type="button"
                aria-label={`Go to banner ${index + 1}`}
                aria-current={
                  isActive
                    ? 'true'
                    : undefined
                }
                onClick={() =>
                  goTo(index)
                }
                className="
                  h-[8px]
                  w-[8px]

                  border-0
                  p-0

                  transition-all
                  duration-300

                  sm:h-[9px]
                  sm:w-[9px]
                "
                style={{
                  backgroundColor:
                    isActive
                      ? MAROON
                      : 'rgba(43,0,0,0.18)',

                  transform:
                    isActive
                      ? 'rotate(45deg) scale(1.08)'
                      : 'rotate(45deg)',
                }}
              />
            )
          })}
        </div>
      )}

      {/* ======================================================
          CAROUSEL CSS

          DESKTOP:
          Previous visible on left
          Current large in center
          Next visible on right

          MOBILE:
          Same concept but smaller side previews
          ====================================================== */}

      <style>{`

        /* =====================================================
           BASE / MOBILE
           ===================================================== */

        .hero-carousel-slide {
          width: 82%;

          opacity: 1;

          will-change:
            transform,
            opacity;
        }

        .hero-carousel-active {
          z-index: 30;

          transform:
            translateX(-50%)
            scale(1);

          opacity: 1;

          cursor: default;

          box-shadow:
            0 8px 24px
            rgba(43, 0, 0, 0.08);
        }

        .hero-carousel-previous {
          z-index: 10;

          transform:
            translateX(-154%)
            scale(0.97);

          opacity: 1;

          cursor: pointer;
        }

        .hero-carousel-next {
          z-index: 10;

          transform:
            translateX(54%)
            scale(0.97);

          opacity: 1;

          cursor: pointer;
        }

        .hero-carousel-hidden {
          z-index: 0;

          transform:
            translateX(-50%)
            scale(0.94);

          opacity: 0;

          pointer-events: none;
        }


        /* =====================================================
           TABLET
           ===================================================== */

        @media (min-width: 640px) {

          .hero-carousel-slide {
            width: 80%;
          }

          .hero-carousel-previous {
            transform:
              translateX(-155%)
              scale(0.975);
          }

          .hero-carousel-next {
            transform:
              translateX(55%)
              scale(0.975);
          }
        }


        /* =====================================================
           LAPTOP / DESKTOP

           This is the closest to your Tanishq screenshot.
           ===================================================== */

        @media (min-width: 1024px) {

          .hero-carousel-slide {
            width: 82%;
          }

          .hero-carousel-active {
            transform:
              translateX(-50%)
              scale(1);

            box-shadow:
              0 10px 30px
              rgba(43, 0, 0, 0.07);
          }

          .hero-carousel-previous {
            transform:
              translateX(-153.5%)
              scale(1);
          }

          .hero-carousel-next {
            transform:
              translateX(53.5%)
              scale(1);
          }
        }


        /* =====================================================
           LARGE DESKTOP
           ===================================================== */

        @media (min-width: 1440px) {

          .hero-carousel-slide {
            width: 82%;
          }

          .hero-carousel-previous {
            transform:
              translateX(-153%)
              scale(1);
          }

          .hero-carousel-next {
            transform:
              translateX(53%)
              scale(1);
          }
        }


        /* =====================================================
           REDUCED MOTION
           ===================================================== */

        @media (
          prefers-reduced-motion:
          reduce
        ) {

          .hero-carousel-slide {
            transition: none;
          }
        }

      `}</style>
    </section>
  )
}
